/**
 * Regole di dominio di Audit Plan, condivise da backend e frontend.
 * Fonte: mockup/v2-2026-10-19.html (stesse costanti, stessi calcoli, stessa matrice permessi).
 */

import { defaultNotificationsConfig, mergeNotifications, type NotificationsConfig } from "./notifications.js";

export const ROLES = ["admin", "manager", "auditor", "supplier", "viewer"] as const;
export type Role = (typeof ROLES)[number];

/** semi_announced (AUD-F8.4): il fornitore conosce una finestra di date (plannedDate .. plannedDateTo), non il giorno. */
export const AUDIT_TYPES = ["initial", "periodic", "followup", "unannounced", "semi_announced"] as const;
export type AuditType = (typeof AUDIT_TYPES)[number];

export const AUDIT_ORIGINS = ["program", "client"] as const;
export type AuditOrigin = (typeof AUDIT_ORIGINS)[number];

export const AUDIT_FLOW = ["planned", "confirmed", "in_progress", "completed", "in_validation", "closed"] as const;
export type AuditStatus = (typeof AUDIT_FLOW)[number];

/** Transizioni ammesse (from -> to). in_validation -> in_progress = rimanda all'auditor. */
export const AUDIT_TRANSITIONS: Record<AuditStatus, AuditStatus[]> = {
  planned: ["confirmed"],
  confirmed: ["in_progress"],
  in_progress: ["completed"],
  completed: ["in_validation", "in_progress"],
  in_validation: ["closed", "in_progress"],
  closed: [],
};

export const ACT_FLOW = ["open", "progress", "verify", "done", "closed"] as const;
export type ActionStatus = (typeof ACT_FLOW)[number];
export const ACTION_TRANSITIONS: Record<ActionStatus, ActionStatus[]> = {
  open: ["progress"],
  progress: ["verify"],
  verify: ["done", "progress"], // verify -> progress = rifiuto con nota
  done: ["closed"],
  closed: [],
};

export const CAP_STATUSES = ["open", "in_progress", "closed"] as const;
export type CapStatus = (typeof CAP_STATUSES)[number];

/** Sezioni valutate della checklist Brandart (rev. 0 del 01/09/2025) + X = Informazioni aggiuntive (sì/no, non valutate). */
export const SECS = ["S", "H", "E"] as const;
export type ScoredSection = (typeof SECS)[number];
export const XSEC = "X" as const;
export const SECTIONS = ["S", "H", "E", "X"] as const;
export type Section = (typeof SECTIONS)[number];

export const ANS = ["C", "NC_MINOR", "NC_MAJOR", "NC_CRITICAL", "OSS", "NA"] as const;
export type AnswerResult = (typeof ANS)[number];
export const isNC = (r?: string | null): r is "NC_MINOR" | "NC_MAJOR" | "NC_CRITICAL" => /^NC_/.test(r || "");

export const NC_SEVERITIES = ["obs", "minor", "major", "critical", "zero"] as const;
export type NcSeverity = (typeof NC_SEVERITIES)[number];
export const NC_SEV: Record<"NC_MINOR" | "NC_MAJOR" | "NC_CRITICAL", NcSeverity> = {
  NC_MINOR: "minor",
  NC_MAJOR: "major",
  NC_CRITICAL: "critical",
};

export const RISKS = ["low", "medium", "high"] as const;
export type Risk = (typeof RISKS)[number];
export const PRIORITIES = ["low", "medium", "high"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const EVIDENCE_STATUSES = ["pending", "approved", "rejected"] as const;
export type EvidenceStatus = (typeof EVIDENCE_STATUSES)[number];
export const EVIDENCE_TARGETS = ["supplier", "audit", "question", "nc", "action", "cap"] as const;
export type EvidenceTargetType = (typeof EVIDENCE_TARGETS)[number];
export const EVIDENCE_EXT = ["pdf", "jpg", "jpeg", "png", "heic", "heif", "docx", "xlsx"] as const;
export const EVIDENCE_MIME: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  heif: "image/heif",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};
export const EVIDENCE_MAX_BYTES = 20 * 1024 * 1024;

export const CHECKLIST_VERSION_CODE = "rev0-2025-09-01";
export const CHECKLIST_VERSION_TITLE = "Checklist Supply Chain rev. 0 del 01/09/2025";

export const ZERO_ACTIONS = ["none", "ratingD", "phaseout"] as const;
export type ZeroAction = (typeof ZERO_ACTIONS)[number];
/** Rating del fornitore derivato dagli stabilimenti (AUD-F2.15): il peggiore, la media, o quello dell'ultimo audit chiuso. */
export const SUPPLIER_RATING_RULES = ["worst", "average", "latest"] as const;
export type SupplierRatingRule = (typeof SUPPLIER_RATING_RULES)[number];
export const CODE_YEAR_MODES = ["planned", "current"] as const;
export type CodeYearMode = (typeof CODE_YEAR_MODES)[number];

/** Regole del rating (decisione 19/10): cosa succede con una NC critical o su una domanda zero tolerance. */
export interface ZeroRule {
  onNcCritical: ZeroAction;
  onZeroTolerance: ZeroAction;
}
/** Tempi di conservazione dei dati (GDPR): il job elenca, non cancella. */
export interface RetentionConfig {
  evidenceYears: number;
  auditLogYears: number;
  closedAuditYears: number;
  inactiveSupplierUserMonths: number;
}
export interface AppConfig {
  weights: Record<ScoredSection, number>;
  visits: Record<Risk, number>;
  functionHead: { name: string; email: string };
  managerEsc: boolean;
  zeroRule: ZeroRule;
  /** Come si ricava il rating del fornitore da quelli dei suoi stabilimenti. */
  supplierRating: SupplierRatingRule;
  /** Anno nel codice audit (AUD-AAAA-NNN): della data pianificata o quello corrente. */
  auditCodeYear: CodeYearMode;
  /** Classe di rischio e copertura visite visibili (RAT26, Pier 27/10: nascoste finché la classe non è compilata). Il dato resta. */
  showRisk: boolean;
  /**
   * RAT26-F3.1b: fornitori e stabilimenti nuovi dalla sync del VendorHub entrano disattivati (senza rating). Per ambiente: la
   * configurazione sta nel DB di ognuno (Mario 28/10: in produzione restano tutti attivi, sul test disattivati).
   */
  vendorhubNewInactive: boolean;
  retention: RetentionConfig;
  /** Quando e come inviare le notifiche (Amministrazione → Notifiche). */
  notifications: NotificationsConfig;
}
export const DEFAULT_CONFIG: AppConfig = {
  weights: { S: 35, H: 35, E: 30 },
  visits: { high: 3, medium: 2, low: 1 },
  functionHead: { name: "", email: "" },
  managerEsc: true,
  zeroRule: { onNcCritical: "ratingD", onZeroTolerance: "phaseout" },
  supplierRating: "worst",
  auditCodeYear: "current",
  showRisk: true,
  vendorhubNewInactive: true,
  retention: { evidenceYears: 10, auditLogYears: 10, closedAuditYears: 10, inactiveSupplierUserMonths: 24 },
  notifications: defaultNotificationsConfig(),
};

/** Unisce una configurazione (anche parziale o vecchia) ai default, sezione per sezione. */
export function mergeConfig(data?: (Partial<Omit<AppConfig, "notifications">> & { notifications?: Parameters<typeof mergeNotifications>[0] }) | null): AppConfig {
  const d = data ?? {};
  const notifications = mergeNotifications(d.notifications);
  // configurazioni precedenti alle notifiche configurabili: lo spegnimento dell'escalation al manager passa al nuovo evento
  if (d.managerEsc === false && !d.notifications?.events?.escalation15) notifications.events.escalation15.enabled = false;
  return {
    ...DEFAULT_CONFIG,
    ...d,
    weights: { ...DEFAULT_CONFIG.weights, ...(d.weights ?? {}) },
    visits: { ...DEFAULT_CONFIG.visits, ...(d.visits ?? {}) },
    functionHead: { ...DEFAULT_CONFIG.functionHead, ...(d.functionHead ?? {}) },
    zeroRule: { ...DEFAULT_CONFIG.zeroRule, ...(d.zeroRule ?? {}) },
    retention: { ...DEFAULT_CONFIG.retention, ...(d.retention ?? {}) },
    notifications,
  };
}

export interface Scores {
  S: number | null;
  H: number | null;
  E: number | null;
  total: number | null;
}

/**
 * Score per sezione: punti delle C e OSS sul totale delle risposte valutate (NA e non risposte escluse).
 * Totale: media pesata sulle sezioni con almeno una risposta valutata.
 */
export function calcScores(
  questions: { id: string; section: string; points: number; active?: boolean }[],
  answers: Record<string, { r?: string | null } | undefined>,
  weights: Record<ScoredSection, number> = DEFAULT_CONFIG.weights,
): Scores {
  const out: Scores = { S: null, H: null, E: null, total: null };
  let wsum = 0;
  let tot = 0;
  for (const sec of SECS) {
    let got = 0;
    let max = 0;
    for (const q of questions) {
      if (q.section !== sec || q.active === false) continue;
      const a = answers[q.id];
      if (!a || !a.r || a.r === "NA") continue;
      const p = +q.points || 10;
      max += p;
      if (a.r === "C" || a.r === "OSS") got += p;
    }
    out[sec] = max ? Math.round((got / max) * 1000) / 10 : null;
    if (out[sec] != null) {
      wsum += +weights[sec];
      tot += (out[sec] as number) * weights[sec];
    }
  }
  out.total = wsum ? Math.round((tot / wsum) * 10) / 10 : null;
  return out;
}

export type Rating = "A" | "B" | "C" | "D" | "NQ" | "-";
/** Override della classe: "D" per regola di configurazione, "NQ" (non qualificato) per un audit con esito negativo (RAT26-F1.1). */
export type RatingOverride = "D" | "NQ";
export const asOverride = (x: string | null | undefined): RatingOverride | null => (x === "D" || x === "NQ" ? x : null);
export const ratingClass = (s: number | null | undefined): Rating =>
  s == null ? "-" : s >= 85 ? "A" : s >= 70 ? "B" : s >= 55 ? "C" : "D";

/** Classe da score con l'eventuale override (ratingOverride "D" o "NQ"). */
export const ratingOf = (score: number | null | undefined, override?: string | null): Rating => asOverride(override) ?? ratingClass(score);

export interface FactoryRating {
  rating: number | null;
  ratingOverride: string | null;
  ratingAt: string | null;
}
/**
 * Rating del fornitore dai rating dei suoi stabilimenti (solo quelli con un audit chiuso).
 * worst: lo stabilimento peggiore (non qualificato vince, poi un override D); average: media degli score, NQ o D se tutti
 * gli stabilimenti lo sono; latest: l'ultimo per data.
 */
export function deriveSupplierRating(factories: FactoryRating[], rule: SupplierRatingRule): FactoryRating {
  const rated = factories.filter((f) => f.rating != null || asOverride(f.ratingOverride));
  if (!rated.length) return { rating: null, ratingOverride: null, ratingAt: null };
  const lastAt = rated.map((f) => f.ratingAt ?? "").sort().pop() || null;
  if (rule === "latest") {
    const f = [...rated].sort((a, b) => (a.ratingAt ?? "").localeCompare(b.ratingAt ?? "")).pop()!;
    return { rating: f.rating, ratingOverride: asOverride(f.ratingOverride), ratingAt: f.ratingAt };
  }
  const scores = rated.map((f) => f.rating).filter((x): x is number => x != null);
  const all = (o: RatingOverride) => rated.every((f) => f.ratingOverride === o);
  const any = (o: RatingOverride) => rated.some((f) => f.ratingOverride === o);
  if (rule === "average") {
    const avg = scores.length ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : null;
    return { rating: avg, ratingOverride: all("NQ") ? "NQ" : all("D") ? "D" : null, ratingAt: lastAt };
  }
  return { rating: scores.length ? Math.min(...scores) : null, ratingOverride: any("NQ") ? "NQ" : any("D") ? "D" : null, ratingAt: lastAt };
}

export interface RuleHit {
  questionId: string;
  code: string;
  result: AnswerResult;
  /** "critical" = NC critical; "zero" = NC su domanda zero tolerance */
  kind: "critical" | "zero";
}
export interface RatingRulesResult {
  /** "D" se una regola forza il rating a D, altrimenti null. */
  ratingOverride: "D" | null;
  ratingReason: string | null;
  /** Se una regola chiede il phase-out: l'audit lo propone (non lo applica). */
  suggestedOutcome: { outcome: "phaseout"; reason: string; questionId: string } | null;
  hits: RuleHit[];
}

/**
 * Applica le regole di configurazione (zeroRule) alle risposte: NC_CRITICAL (onNcCritical) e NC_* su domande zero tolerance
 * (onZeroTolerance). "ratingD" forza il rating a D (lo score resta quello calcolato); "phaseout" propone l'esito phase-out.
 */
export function evalRatingRules(
  questions: { id: string; code?: string; section: string; active?: boolean; zeroTolerance?: boolean }[],
  answers: Record<string, { r?: string | null } | undefined>,
  rule: ZeroRule = DEFAULT_CONFIG.zeroRule,
): RatingRulesResult {
  const hits: RuleHit[] = [];
  for (const q of questions) {
    if (q.section === "X") continue;
    const a = answers[q.id];
    if (!a || !isNC(a.r)) continue;
    if (a.r === "NC_CRITICAL") hits.push({ questionId: q.id, code: q.code ?? q.id, result: a.r, kind: "critical" });
    if (q.zeroTolerance) hits.push({ questionId: q.id, code: q.code ?? q.id, result: a.r, kind: "zero" });
  }
  const actionOf = (h: RuleHit) => (h.kind === "critical" ? rule.onNcCritical : rule.onZeroTolerance);
  const why = (h: RuleHit) => (h.kind === "critical" ? `NC critical su ${h.code}` : `NC su domanda zero tolerance ${h.code}`);
  const dHits = hits.filter((h) => actionOf(h) === "ratingD");
  const poHits = hits.filter((h) => actionOf(h) === "phaseout");
  const join = (hs: RuleHit[]) => [...new Set(hs.map(why))].join("; ");
  return {
    ratingOverride: dHits.length ? "D" : null,
    ratingReason: dHits.length ? `Rating D per regola: ${join(dHits)}` : null,
    suggestedOutcome: poHits.length ? { outcome: "phaseout", reason: `Phase-out proposto per regola: ${join(poHits)}`, questionId: poHits[0]!.questionId } : null,
    hits,
  };
}

/** Visite richieste nell'anno per classe di rischio (regola demo alto 3, medio 2, basso 1). */
export const reqVisits = (risk: Risk, cfg: AppConfig = DEFAULT_CONFIG) => +cfg.visits[risk] || 1;

/** Stato nel piano audit (vista calendario). */
/**
 * Stato VendorHub del fornitore nell'elenco (AUD-F8.1): lo stato del vendor nel VendorHub è sempre «in scouting», la qualifica
 * sta sugli stabilimenti. Vale il peggiore: Disqualified, poi qualifica in corso, poi da allineare, poi gli altri, Qualified per ultimo.
 */
export function worstFactoryStatus(statuses: string[]): string | null {
  const rank = (s: string) => (/disqualified/i.test(s) ? 0 : /prequalification/i.test(s) ? 1 : /^qualified$/i.test(s) ? 3 : 2);
  return [...statuses].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))[0] ?? null;
}

/** Stato nel piano annuale: «Chiuso» e «Completato» sono uno stato solo, Completato (review di Pier, AUD-F8.1). */
export type PlanState = "planned" | "exec" | "done";
export const planState = (a: { status: AuditStatus; outcome?: string | null }): PlanState =>
  a.outcome === "phaseout" ? "done" : a.status === "planned" || a.status === "confirmed" ? "planned" : a.status === "in_progress" ? "exec" : "done";

/** Decisione 7: il fornitore non vede gli audit non annunciati finché non sono avviati (né la loro data). */
export const unannHidden = (a: { type: AuditType; status: AuditStatus }) =>
  a.type === "unannounced" && (a.status === "planned" || a.status === "confirmed");

export const isOverdue = (a: { dueDate?: string | null; status: ActionStatus }, today: string) =>
  !!a.dueDate && a.status !== "done" && a.status !== "closed" && a.dueDate.slice(0, 10) < today;

/**
 * Monitoraggio delle NC sospeso (AUD-F8.4, richiesta di Pier del 02/11/2026): per un fornitore o uno stabilimento chiuso o
 * non attivo niente scadenze, promemoria né escalation. Chiuso o non attivo = fornitore in phase-out, fornitore o stabilimento
 * disattivato nell'Audit Plan, stabilimento squalificato nel VendorHub. Ritorna il motivo, null se il monitoraggio è attivo.
 */
export type MonitoringOff = "phaseout" | "supplier_inactive" | "factory_inactive" | "factory_disqualified";
export function monitoringOff(x: {
  supplier: { phaseOut: boolean; active: boolean };
  factory?: { active: boolean; status: string } | null;
}): MonitoringOff | null {
  if (x.supplier.phaseOut) return "phaseout";
  if (!x.supplier.active) return "supplier_inactive";
  if (x.factory && !x.factory.active) return "factory_inactive";
  if (x.factory && /disqualified/i.test(x.factory.status)) return "factory_disqualified";
  return null;
}

/* ---------------- permessi (matrice del mockup) ---------------- */

export interface Actor {
  id: string;
  role: Role;
  supplierId?: string | null;
}
export type Op =
  | "admin"
  | "supplier.edit"
  | "audit.create"
  | "audit.confirm"
  | "plan.view"
  | "audit.exec"
  | "audit.validate"
  | "nc.edit"
  | "action.create"
  | "action.review"
  | "action.supplier"
  | "evidence.upload"
  | "data.edit";

/** Oggetto di contesto: un audit (auditorId, type, supplierId) o qualcosa con supplierId. */
export interface PermCtx {
  auditorId?: string | null;
  type?: AuditType;
  supplierId?: string | null;
}

export function can(actor: Actor | null | undefined, op: Op, o: PermCtx = {}): boolean {
  if (!actor) return false;
  const r = actor.role;
  const isAud = !!o.auditorId && o.auditorId === actor.id;
  switch (op) {
    case "admin":
      return r === "admin";
    case "supplier.edit":
    case "audit.create":
    case "audit.validate":
      return r === "admin" || r === "manager";
    case "audit.confirm":
      return o.type === "unannounced"
        ? r === "admin" || r === "manager" || (r === "auditor" && isAud)
        : r === "admin" || r === "manager" || (r === "supplier" && o.supplierId === actor.supplierId);
    case "plan.view":
      return r !== "supplier";
    case "audit.exec":
      return r === "admin" || r === "manager" || (r === "auditor" && isAud);
    case "nc.edit":
    case "action.create":
    case "action.review":
      return r === "admin" || r === "manager" || (r === "auditor" && isAud);
    case "action.supplier":
      return r === "admin" || (r === "supplier" && o.supplierId === actor.supplierId);
    case "evidence.upload":
      return r !== "viewer" && (r !== "supplier" || o.supplierId === actor.supplierId);
    case "data.edit":
      return r === "admin" || r === "manager" || r === "auditor";
    default:
      return false;
  }
}

/** Codici progressivi per anno: AUD-2026-001, NC-2026-001, AC-2026-001. */
export function nextCode(prefix: "AUD" | "NC" | "AC", existing: string[], year = new Date().getFullYear()) {
  const head = `${prefix}-${year}-`;
  const n = Math.max(0, ...existing.filter((c) => c.startsWith(head)).map((c) => parseInt(c.split("-")[2]!, 10) || 0)) + 1;
  return `${head}${String(n).padStart(3, "0")}`;
}
